import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import "@/index.css";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import AuthCallback from "@/components/AuthCallback";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import WorkPage from "@/pages/WorkPage";
import ContactPage from "@/pages/ContactPage";
import BookPage from "@/pages/BookPage";
import PayPage from "@/pages/PayPage";
import PaymentResult from "@/pages/PaymentResult";
import InsightsPage from "@/pages/InsightsPage";
import ArticlePage from "@/pages/ArticlePage";
import NotFoundPage from "@/pages/NotFoundPage";
import Enquiries from "@/pages/Enquiries";

export const scrollToSection = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

class ErrorBoundary extends Component {
  componentDidCatch(error) {
    console.error("Section failed to render:", error);
  }

  render() {
    return this.props.children;
  }
}

const Layout = () => (
  <>
    <Nav />
    <main>
      <Outlet />
    </main>
    <Footer />
    <ChatWidget />
  </>
);

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/work" element={<WorkPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/insights/:slug" element={<ArticlePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/book" element={<BookPage />} />
        <Route path="/pay" element={<PayPage />} />
        <Route path="/payment/success" element={<PaymentResult success />} />
        <Route path="/payment/cancel" element={<PaymentResult />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="/enquiries" element={<Enquiries />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="bg-paper text-ink antialiased">
      <div className="grain-overlay" aria-hidden="true" />
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;